import {AbstractInteraction, InteractionState} from "./abstract-interaction";
import {Feature, Point, Position} from "geojson";
import {SnapMode, SnapPoint} from "../snap";
import {GeometryProvider} from "../providers/geometry-provider";
import {FeatureUtil} from "../feature";
import {VueMapPopup} from "../control/VueMapPopup";
import {defineAsyncComponent} from "vue";
import {GeometryUtil} from "../geometry";
import {mapboxglref} from "@/js/mapboxService";

let MapInputNumberMarker = defineAsyncComponent(() => import("../../../components/map/markers/MapInputNumber.vue"));

export class DrawInteractionState extends InteractionState {
    vertexPosition: number;
    vertexList: Position[];
    nextLength: number | undefined;
}

export class AbstractDraw<StateType extends DrawInteractionState> extends AbstractInteraction<DrawInteractionState> {
    public mapboxgl = mapboxglref();
    protected aim: Feature<Point>;
    protected lengthInput: VueMapPopup<typeof MapInputNumberMarker> | undefined;

    getSupportedSnapModes(): SnapMode[] {
        return ['manual', 'snap', 'trace'];
    }


    canSave(state: InteractionState): boolean {
        return false;
    }

    showHistory(): boolean {
        return true;
    }

    isTraceModeAccepted(state: DrawInteractionState): boolean {
        return state.snapFeature !== undefined;
    }

    buildInitialState(initialState: StateType): StateType {
        this.aim = this.createAIM();
        this.displayDrawFeature(this.aim);
        this.doubleClickZoom.disable(this.mode.currentInteractionAPI);

        let state = {
            ...super.buildInitialState(initialState),
            vertexPosition: initialState.vertexPosition ?? 0,
            vertexList: initialState.vertexList ?? [],
        } as StateType;

        if (initialState.snapFeature) {
            state.snapFeature = initialState.snapFeature;
            state.snapFeaturePath = initialState.snapFeaturePath;
        } else if (this.controller.snapUtility.isMode('trace')) {
            this.controller.snapUtility.setMode('snap', false);
        }

        return state;
    }

    onSnapModeChange(state: DrawInteractionState, mode: SnapMode) {
        if (mode !== 'manual') {
            state.nextLength = undefined;
        }
    }

    onStop(state: any) {
        super.onStop(state);

        this.doubleClickZoom.enable(this.mode.currentInteractionAPI);

        if (this.lengthInput) {
            this.lengthInput.remove();
            this.lengthInput = undefined;
        }
    }

    async onClick(state: DrawInteractionState, e: mapboxgl.MapMouseEvent): Promise<void> {

        await super.onClick(state, e);

        if (this._isFinalizeClick(e)) {
            this.save(state);
            e.preventDefault();
            return;
        }

        this.controller.interaction.historyPush(state);

        this.snapPoint = await this._getSnapPoint(state as any, e)

        if (!this.controller.snapUtility.isMode('trace')) {
            if (state.snapFeature && this.snapPoint.type === 'none') {
                state.snapFeature = undefined;
                state.snapFeaturePath = undefined;

            } else if (this.snapPoint.type !== 'none' && this.snapPoint.layerGeometry.geometryId !== state.snapFeature?.geometryId) {
              if(this.snapPoint?.layerGeometry?.document?.properties?.dataSource?.isLocal){

                state.snapFeature =this.snapPoint?.layerGeometry
              }
              else
                state.snapFeature = await GeometryProvider.fetchSingleGeometry(
                    this.snapPoint.layerGeometry.geometryId,
                    this.snapPoint.layerGeometry.document,
                    this.controller.configuration.accessToken,
                );
                this.snapPoint = this.controller.snapUtility.getFeaturePoint(e, state.snapFeature);
                state.snapFeaturePath = this.snapPoint.path;
            }

        } else {
            let trace = FeatureUtil.extractLineFrom(state.snapFeature.geometry as any, state.snapFeaturePath, this.snapPoint.path);
            state.vertexList.push(...trace);
            state.vertexPosition += trace.length;
            state.snapFeaturePath = this.snapPoint.path;
        }

        state.vertexList.push(this.snapPoint.point);
        state.vertexPosition++;
    }

    isManualInputSupported(state: DrawInteractionState): boolean {
        return true;
    }


    getCoordinates(state: DrawInteractionState): {path: string; coordinate: Position}[] {
        return state.vertexList.map((coordinate, index) => {
            return {path: `0.${index}`, coordinate};
        })
    }

    async setCoordinates(state: DrawInteractionState, points: Position[]): Promise<void> {
        this.controller.interaction.historyPush(state);

        state.vertexList = [...points];
        state.vertexPosition = points.length;
    }

    async onMouseMove(state: StateType, e: mapboxgl.MapMouseEvent) {

        this.snapPoint = await this._getSnapPoint(state, e);
        this.aim.geometry.coordinates = this.snapPoint.point as Position;
        this.displayDrawFeature(this.aim);
        this._generateLengthInput(state);
    }

    protected _isFinalizeClick(e: mapboxgl.MapMouseEvent): boolean {
        let features = this.controller.mapInstance.queryRenderedFeatures(e.point);
        return !!features.find((feature => {
            return feature.properties.icon === 'map-check';
        }));
    }

    protected _isMapMarker(e: mapboxgl.MapMouseEvent): boolean {
        return false;
    }

    protected _getSnapPoint(state: StateType, e: mapboxgl.MapMouseEvent): SnapPoint {

        if (this.controller.snapUtility.isMode('trace')) {
            return this.controller.snapUtility.getFeaturePoint(e, state.snapFeature);
        }

        if (state.vertexPosition > 0) {
            let opts: any = {};
            if (this.controller.snapUtility.getMode() === 'manual' && state.nextLength > 0) {
                opts.width = state.nextLength;
            }
            return this.controller.snapUtility.getSnapPoint(e, [], state.vertexList[state.vertexPosition - 1], undefined, opts)
        }

        return this.controller.snapUtility.getSnapPoint(e);
    }

    protected _generateLengthInput(state: DrawInteractionState): void {
        if (this.controller.snapUtility.getMode() === 'manual' && state.vertexPosition > 0) {
            let midpoint = GeometryUtil.midpoint(this.snapPoint.point, state.vertexList[state.vertexPosition - 1]);
            if (!this.lengthInput) {
                this.lengthInput = new VueMapPopup<typeof MapInputNumberMarker>(
                    MapInputNumberMarker,
                    {
                        initialValue: 1,
                        min: 0,
                        styleclass: 'map-input-number-lg',
                        suffix: ' m',
                        onInput: (value: number) => state.nextLength = value
                    }
                )

                this.lengthInput
                    .setLngLat(new this.mapboxgl.LngLat(midpoint[0], midpoint[1]))
                    .addTo(this.controller.mapInstance);
            }
            this.lengthInput.setLngLat(new this.mapboxgl.LngLat(midpoint[0], midpoint[1]));
            if (!state.nextLength) {
                this.lengthInput.ctx()?.setValue(GeometryUtil.calculateLength([this.snapPoint.point, state.vertexList[state.vertexPosition - 1]]));
            }
        } else if (this.lengthInput) {
            this.lengthInput.remove();
            this.lengthInput = undefined;
        }
    }
}
